import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SkipLink, LiveRegion, Announcement } from '../components/ui/Accessibility';
import { Spinner } from '../components/ui/Loading';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Focus email input on mount
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  // Focus error message when it appears
  useEffect(() => {
    if (error) {
      errorRef.current?.focus();
    }
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setAnnouncement('Signing in...');

    try {
      await login(email, password);
      setAnnouncement('Sign in successful. Redirecting...');
      navigate('/artist');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed. Please try again.';
      setError(message);
      setAnnouncement(`Error: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      {/* Skip Link */}
      <SkipLink href="#login-form">Skip to login form</SkipLink>

      {/* Announce page */}
      <LiveRegion>Sign In - Gallery</LiveRegion>

      {/* Dynamic announcements */}
      {announcement && <Announcement message={announcement} />}

      <div className="w-full max-w-md">
        <h1 className="font-display text-4xl font-bold text-center mb-8">Sign In</h1>

        <form
          id="login-form"
          onSubmit={handleSubmit}
          className="space-y-6"
          aria-label="Sign in form"
          noValidate
        >
          {/* Error message */}
          {error && (
            <div
              ref={errorRef}
              role="alert"
              aria-live="assertive"
              tabIndex={-1}
              className="p-4 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm outline-none"
            >
              <span className="sr-only">Error: </span>
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gallery-200 mb-2"
            >
              Email
              <span className="text-red-400 ml-1" aria-hidden="true">*</span>
              <span className="sr-only">(required)</span>
            </label>
            <input
              ref={emailRef}
              id="email"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              aria-required="true"
              aria-describedby={error ? 'login-error' : undefined}
              aria-invalid={error ? 'true' : undefined}
              className="w-full px-4 py-3 bg-gallery-900 border border-gallery-600 rounded-lg text-gallery-100 placeholder-gallery-500 focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none transition-colors"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gallery-200"
              >
                Password
                <span className="text-red-400 ml-1" aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-accent-400 hover:text-accent-300 focus:outline-none focus:ring-2 focus:ring-accent-500 rounded"
              >
                Forgot password?
              </Link>
            </div>
            <input
              id="password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-required="true"
              aria-describedby={error ? 'login-error' : undefined}
              aria-invalid={error ? 'true' : undefined}
              className="w-full px-4 py-3 bg-gallery-900 border border-gallery-600 rounded-lg text-gallery-100 placeholder-gallery-500 focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none transition-colors"
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            aria-busy={loading}
            className="w-full py-3 bg-accent-600 hover:bg-accent-700 disabled:bg-accent-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-accent-400 focus:ring-offset-2 focus:ring-offset-gallery-950 inline-flex items-center justify-center gap-2"
          >
            {loading && <Spinner size="sm" className="text-white" />}
            <span>{loading ? 'Signing in...' : 'Sign In'}</span>
          </button>
        </form>

        <p className="mt-6 text-center text-gallery-300">
          Don't have an account?{' '}
          <Link
            to="/register"
            className="text-accent-400 hover:text-accent-300 font-medium focus:outline-none focus:ring-2 focus:ring-accent-500 rounded"
          >
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
